import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Users, Bot, UserCheck, MessageCircle, Mail, TrendingUp, Target, Building2,
  MapPin, Filter, Download, FileSpreadsheet, FileText, Lock, Globe,
  Smartphone, Activity, MousePointerClick, Repeat, Eye, Loader2,
} from "lucide-react";

const NAVY = "#0B2A6B";
const GOLD = "#D4A24C";
const PIE_COLORS = [NAVY, GOLD, "#6366F1", "#10B981", "#EF4444", "#0EA5E9"];

const RANGES = [
  { k: "today", label: "Today" },
  { k: "yesterday", label: "Yesterday" },
  { k: "7d", label: "Last 7 Days" },
  { k: "30d", label: "Last 30 Days" },
  { k: "90d", label: "Last 90 Days" },
  { k: "custom", label: "Custom" },
];

export default function AdminAnalytics() {
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [data, setData] = useState({ meta: null, summary: null, series: [], sources: [], colleges: null, cities: [], funnel: [] });

  const params = useCallback(() => {
    const p = { range };
    if (range === "custom" && custom.start && custom.end) { p.start = custom.start; p.end = custom.end; }
    return p;
  }, [range, custom]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = params();
      const [meta, summary, ts, src, col, cit, fun] = await Promise.all([
        api.get("/admin/analytics/meta"),
        api.get("/admin/analytics/summary", { params: p }),
        api.get("/admin/analytics/timeseries", { params: p }),
        api.get("/admin/analytics/sources", { params: p }),
        api.get("/admin/analytics/colleges", { params: p }),
        api.get("/admin/analytics/cities", { params: p }),
        api.get("/admin/analytics/funnel", { params: p }),
      ]);
      setData({
        meta: meta.data, summary: summary.data, series: ts.data.series,
        sources: src.data.sources, colleges: col.data, cities: cit.data.cities, funnel: fun.data.leadStatusFunnel,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (range === "custom" && (!custom.start || !custom.end)) return;
    load();
  }, [range, custom, load]);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/admin/analytics/export", { params: { ...params(), format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `collegeunlock-leads.${format === "excel" ? "xlsx" : format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) {
      toast.error("Export failed.");
    } finally {
      setExporting("");
    }
  };

  const od = data.summary?.ownData;
  const ga4On = data.meta?.ga4DataApiConfigured;

  return (
    <div data-testid="admin-analytics-page" className="space-y-8">
      <SEOHead title="Founder Analytics" />

      {/* Header + Filters */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Founder Analytics</h1>
          <p className="text-sm text-slate-500">Complete business health at a glance</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1 text-xs text-slate-500"><Filter className="h-3.5 w-3.5" /></span>
          {RANGES.map((r) => (
            <button
              key={r.k}
              data-testid={`range-${r.k}`}
              onClick={() => setRange(r.k)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${range === r.k ? "bg-[#0B2A6B] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <label className="text-sm">From<input data-testid="custom-start" type="date" className="mt-1 block h-10 rounded-lg border border-slate-300 px-3 text-sm" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} /></label>
          <label className="text-sm">To<input data-testid="custom-end" type="date" className="mt-1 block h-10 rounded-lg border border-slate-300 px-3 text-sm" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} /></label>
        </div>
      )}

      {/* Export bar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-600">Export leads:</span>
        <ExportBtn testid="export-csv" icon={Download} label="CSV" loading={exporting === "csv"} onClick={() => doExport("csv")} />
        <ExportBtn testid="export-excel" icon={FileSpreadsheet} label="Excel" loading={exporting === "xlsx"} onClick={() => doExport("xlsx")} />
        <ExportBtn testid="export-pdf" icon={FileText} label="PDF" loading={exporting === "pdf"} onClick={() => doExport("pdf")} />
      </div>

      {loading || !od ? (
        <div className="flex justify-center py-24"><Loader2 className="h-10 w-10 animate-spin text-[#0B2A6B]" /></div>
      ) : (
        <>
          {/* 1. Executive Overview */}
          <Section title="Lead Acquisition" subtitle="Where your leads come from & how they convert">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi testid="kpi-total-leads" icon={Users} color={NAVY} label="Total Leads" value={od.totalLeads} />
              <Kpi testid="kpi-ai" icon={Bot} color={GOLD} label="AI Counselling" value={od.aiCounselingRequests} />
              <Kpi testid="kpi-expert" icon={UserCheck} color="#10B981" label="Expert Counselling" value={od.expertCounselingRequests} />
              <Kpi testid="kpi-contact" icon={Mail} color="#6366F1" label="Contact Leads" value={od.contactFormLeads} />
              <Kpi testid="kpi-cta" icon={Building2} color="#0EA5E9" label="College Enquiries" value={od.collegeCtaLeads} />
              <Kpi testid="kpi-conversion" icon={Target} color="#EF4444" label="Conversion Rate" value={`${od.conversionRate}%`} hint="Admitted / total" />
              <Kpi testid="kpi-engagement" icon={TrendingUp} color={NAVY} label="Engagement Rate" value={`${od.engagementRate}%`} hint="Contacted+" />
              {ga4On && <GaKpi icon={MousePointerClick} label="WhatsApp Clicks" ga4On={ga4On} />}
            </div>
            {ga4On && (
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <GaKpi icon={Users} label="Total Visitors" ga4On={ga4On} />
                <GaKpi icon={Activity} label="Active Users" ga4On={ga4On} />
                <GaKpi icon={Eye} label="Page Views" ga4On={ga4On} />
                <GaKpi icon={Repeat} label="Returning Users" ga4On={ga4On} />
              </div>
            )}
          </Section>

          {/* Lead trend + source split */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2" title="Lead Trend" testid="lead-trend-chart">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.series} margin={{ left: -20, right: 10, top: 10 }}>
                  <defs>
                    <linearGradient id="gTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={NAVY} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={NAVY} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="total" stroke={NAVY} strokeWidth={2} fill="url(#gTotal)" name="Total leads" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Lead Sources" testid="lead-sources-chart">
              {data.sources.some((s) => s.count > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={data.sources.filter((s) => s.count > 0)} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.pct}%`}>
                      {data.sources.filter((s) => s.count > 0).map((s, i) => <Cell key={s.source} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty>No leads in range</Empty>}
            </Card>
          </div>

          {/* College performance */}
          {(data.colleges?.topByEnquiry?.length || data.colleges?.topByAIRecommendation?.length) ? (
            <Section title="College Performance" subtitle="From enquiries & AI recommendations">
              <div className="grid lg:grid-cols-2 gap-6">
                {data.colleges?.topByEnquiry?.length ? (
                  <Card title="Top Colleges by Enquiry" testid="college-enquiry-chart">
                    <HBar data={data.colleges.topByEnquiry} color={GOLD} />
                  </Card>
                ) : null}
                {data.colleges?.topByAIRecommendation?.length ? (
                  <Card title="Most AI-Recommended Colleges" testid="college-airec-chart">
                    <HBar data={data.colleges.topByAIRecommendation} color={NAVY} />
                  </Card>
                ) : null}
              </div>
            </Section>
          ) : null}

          {/* City performance + Funnel */}
          {(data.cities.length > 0 || data.funnel.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-6">
              {data.cities.length > 0 && (
                <Card title="City Performance" testid="city-performance">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="text-xs uppercase text-slate-400"><th className="py-2">City</th><th className="py-2">Leads</th><th className="py-2">Conversion</th></tr></thead>
                      <tbody>
                        {data.cities.map((c) => (
                          <tr key={c.city} className="border-t border-slate-100">
                            <td className="py-2 font-medium text-slate-800 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#D4A24C]" /> {c.city}</td>
                            <td className="py-2 text-slate-600">{c.count}</td>
                            <td className="py-2 text-slate-600">{c.conversionRate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              {data.funnel.length > 0 && (
                <Card title="Lead Status Funnel" testid="lead-funnel">
                  <div className="space-y-3">
                    {data.funnel.map((s, i) => (
                      <div key={s.step}>
                        <div className="flex justify-between text-sm"><span className="text-slate-600">{s.step}</span><span className="font-semibold text-slate-900">{s.count} · {s.pct}%</span></div>
                        <div className="mt-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: i === 0 ? NAVY : i === 3 ? "#10B981" : GOLD }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* GA4-powered sections — shown only once GA4 Data API is configured */}
          {ga4On && (
            <Section title="Traffic, Audience & Real-Time" subtitle="Powered by Google Analytics 4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Ga4Card icon={TrendingUp} title="Traffic Analytics" desc="Daily/weekly/monthly visitors, sessions, bounce rate, avg. session duration, pages/session." on={ga4On} />
                <Ga4Card icon={Globe} title="Traffic Sources" desc="Organic, Direct, Social (LinkedIn/Instagram/Facebook), Google, Referral, WhatsApp." on={ga4On} />
                <Ga4Card icon={MapPin} title="Geographic Analytics" desc="Visitors by country, state & city with charts and table." on={ga4On} />
                <Ga4Card icon={Smartphone} title="Device Analytics" desc="Desktop / Mobile / Tablet, browser, OS, screen resolution." on={ga4On} />
                <Ga4Card icon={Activity} title="Engagement & Events" desc="Most viewed pages, exit pages, scroll depth, key conversion events." on={ga4On} />
                <Ga4Card icon={MousePointerClick} title="Real-Time Dashboard" desc="Active users, current page, source, device, city, live event stream." on={ga4On} />
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-4"><h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>{subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}</div>
      {children}
    </div>
  );
}

function Card({ title, testid, children, className = "" }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      {title && <h3 className="mb-4 font-display font-bold text-slate-900">{title}</h3>}
      {children}
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, hint, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${color}1a` }}><Icon className="h-5 w-5" style={{ color }} /></span>
      <p className="mt-3 font-display text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function GaKpi({ icon: Icon, label, ga4On }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-200"><Icon className="h-5 w-5 text-slate-500" /></span>
      <p className="mt-3 font-display text-base font-bold text-slate-400 flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> {ga4On ? "—" : "GA4"}</p>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-[10px] text-slate-400">{ga4On ? "Loading…" : "Connect GA4"}</p>
    </div>
  );
}

function Ga4Card({ icon: Icon, title, desc, on }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
      <div className="flex items-center justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-slate-200"><Icon className="h-5 w-5 text-[#0B2A6B]" /></span>
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{on ? "GA4 Connected" : "Connect GA4"}</span>
      </div>
      <h3 className="mt-3 font-display font-bold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">{desc}</p>
      {!on && <p className="mt-2 text-[11px] text-slate-400">Add GA4 Data API credentials (Property ID + service account) to enable real data here.</p>}
    </div>
  );
}

function HBar({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 38)}>
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
        <Tooltip />
        <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} barSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Empty({ children }) {
  return <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">{children}</div>;
}

function ExportBtn({ icon: Icon, label, onClick, loading, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4 text-[#0B2A6B]" />} {label}
    </button>
  );
}
